"use client";

import { useEffect, useEffectEvent, useRef } from "react";

import { useStatsigClient } from "@statsig/react-bindings";
import { getCookie } from "cookies-next";
import { useSearchParams } from "next/navigation";

import {
  AppModalKey,
  type AppModalProps,
  ListingRecommendationsVariant,
  useAppModal,
} from "~/modals";

const VENDORS_EXPERIMENT_CITY = "chicago";

const VENDORS_EXPERIMENT_USE_TYPES = ["off-site", "production", "event"];

type InquiryRecommendations = Omit<
  AppModalProps[AppModalKey.ListingRecommendationsModal],
  "onClose" | "variant"
>;

/** Drops `?inquiry=true` on the client only, so the page data is not fetched again. */
const removeInquiryParam = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete("inquiry");
  window.history.pushState({}, "", url.toString());
};

/**
 * Opens the recommendations modal while the URL carries `?inquiry=true`, which the
 * inquiry flow lands back on the listing with. The query param stays the trigger, so
 * the back button and reloads keep working; closing the modal drops it.
 */
const useInquiryRecommendationsModal = (
  recommendations: InquiryRecommendations,
) => {
  const searchParams = useSearchParams();
  const isInquiry = searchParams.get("inquiry") === "true";
  const recommendationsModal = useAppModal(
    AppModalKey.ListingRecommendationsModal,
  );
  const { getExperiment } = useStatsigClient();

  // The experiment is evaluated, and logged, once per page.
  const variantRef = useRef<ListingRecommendationsVariant | null>(null);

  const isEligibleForVendorsExperiment =
    recommendations.listingCity.toLowerCase() === VENDORS_EXPERIMENT_CITY &&
    VENDORS_EXPERIMENT_USE_TYPES.includes(
      recommendations.useType.toLowerCase(),
    );

  /** The variant to show, or null while the experiment cannot be evaluated. */
  const getVariant = () => {
    if (!isEligibleForVendorsExperiment) {
      return ListingRecommendationsVariant.Listings;
    }

    // Without a user the experiment cannot be evaluated, and nothing is shown.
    const userId = getCookie("PSUser") as string | undefined;
    if (!variantRef.current && userId) {
      const showVendors = getExperiment("vendors_post_inquiry").get(
        "variant",
        false,
      );
      variantRef.current = showVendors
        ? ListingRecommendationsVariant.ListingsAndVendors
        : ListingRecommendationsVariant.Listings;
      window.analyticsIntegrations.experimentLoaded(
        "vendors_post_inquiry",
        showVendors ? "true" : "false",
        recommendations.useType,
      );
    }

    return variantRef.current;
  };

  // An effect event, so it opens the modal with the latest props without the effect
  // re-running on them; the modal hook hands out new functions every render.
  const syncModal = useEffectEvent(() => {
    const variant =
      isInquiry && recommendations.recommendedListings.length > 0
        ? getVariant()
        : null;

    if (variant) {
      recommendationsModal.openModal({
        ...recommendations,
        onClose: removeInquiryParam,
        variant,
      });
    } else {
      recommendationsModal.closeModal();
    }
  });

  // The count too: on a `?inquiry=true` load the recommendations stream in after the
  // first render. The count, as the array is sliced afresh every render.
  useEffect(() => {
    syncModal();
  }, [isInquiry, recommendations.recommendedListings.length]);
};

export { useInquiryRecommendationsModal };
